<template>
  <div>
    <section class="ecom left">
      <h1 class="main-header">
        <span class="main-header__icon">
          <img :src="`${cdn}ecomm-icon.svg`" alt="shopping cart">
        </span>
        <span v-if="brand === 'BD'" class="main-header__text">
          Starting a new project?
          <br>
          Rent a dumpster online!
        </span>
        <span v-else class="main-header__text">
          Dumpster rentals made easy.
          <br>
          Order online now!
        </span>
      </h1>
      <AddressSearch />
      <StepperCard
        path="/cart/service"
        :number=2
        title="Service Info &amp; Placement"
        :isFutureStep="true"
      />
      <StepperCard
        :number=3
        title="Payment Information"
        :isFutureStep="true"
        path="/cart/payment"
      />
    </section>
    <!-- Lock Rate Expired Modal -->
    <modal class="lockrate-modal-expired" :isOpen=isLockRateExpiredOpen @close-modal="closeRateModal" :showClose="true">
      <template v-slot:content>
        <div>
          <img class="lockrate-modal__img" :src="themeLogo" />
          <h3 class="lockrate-modal__heading">Oh no! It looks like your rate has expired.</h3>
          <p>We're sorry, but it has been more than 14 days since you saved your dumpster rental quote.</p>
          <p>Give us a call at <span class="lockrate-modal__phone"><a :href="`tel:+${phoneNumber}`">{{phoneNumber}}</a></span> during our regular business hours, and we may be able to honor your original rate. Or, click below to browse our current dumpster availability and get your rental scheduled today.</p>
            <button v-on:click="closeRateModal" class="btn mt-1 lockrate-modal__close">
              Start a New Order
            </button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
// @ is an alias to /src
import axios from 'axios'
import AddressSearch from '@/components/forms/AddressSearch.vue'
import StepperCard from '@/components/StepperCard.vue'
import Modal from '@/components/Modal.vue'
import { timestamp, isGoogleEventsReady, getClientID, getQueryString } from '@/utilities.js'
import config from '@/config'
import { fireAddressSearchEvent } from '../utilities'
const { phoneNumber, cdn } = config

export default {
  name: 'Home',
  components: {
    AddressSearch,
    StepperCard,
    Modal
  },
  data () {
    return {
      isLockRateExpiredOpen: false,
      phoneNumber,
      cdn,
      brand: process.env.VUE_APP_BRAND,
      themeLogo: `${cdn}theme-logo.png`
    }
  },
  beforeRouteEnter (to, from, next) {
    if (from.name === 'Confirmation') {
      location.reload() // If user came from confirmation, reload to wipe state clean
    }
    next()
  },
  methods: {
    closeRateModal () {
      this.isLockRateExpiredOpen = false
    },
    returningFromSavedCart () {
      const UUID = getQueryString('uuid')
      const utmSource = getQueryString('utm_source')
      const utmMedium = getQueryString('utm_medium')
      const utmCampaign = getQueryString('utm_campaign')

      if (UUID) {
        // TODO: replace this with hubRequest from utilities.js
        return axios.get(`${process.env.VUE_APP_SERVER}/api/savedCart`, { params: { UUID, utmSource, utmMedium, utmCampaign } })
          .then(res => {
            const savedCart = res.data.row && res.data.row[0]
            if (savedCart) {
              const cart = savedCart.product
              const expiresAt = savedCart.expires_at
              const createdAt = savedCart.created_at
              const cartStillValid = (new Date() <= new Date(expiresAt)) && !savedCart.is_expired
              if (cartStillValid) {
                const makoResponse = savedCart.mako_response
                this.$store.commit('dumpster/makoResponse', makoResponse)
                this.$store.commit('dumpster/setSelectedDumpsterSize', cart?.item?.size || savedCart.product.selectedDumpsterSize)
                this.$store.commit('dumpster/setSelectedDumpster', savedCart.product.selectedDumpster)
                this.$store.commit('cart/item', cart.item)
                this.$store.commit('cart/savedCartUUID', UUID)
                this.$store.commit('cart/savedCartCreatedAt', createdAt)
                this.$store.commit('cart/savedCartUtmSource', utmSource)
                this.$store.commit('cart/savedCartUtmMedium', utmMedium)
                this.$store.commit('cart/savedCartUtmCampaign', utmCampaign)
                this.$store.commit('customer/email', savedCart.contact.email)
                this.$store.commit('customer/fname', savedCart.contact.fname)
                this.$store.commit('customer/lname', savedCart.contact.lname)
                this.$store.commit('customer/phoneNumber', savedCart.contact.phoneNumber)
                this.$store.commit('customer/serviceAddress', savedCart.delivery.serviceAddress)
                this.$store.commit('customer/isServiceAddressValid', true)
                // billingSameAsDelivery must be set after isServiceAddressValid so that isBillingAddressValid is set accordingly
                this.$store.commit('customer/billingSameAsDelivery', savedCart.billing_same_delivery)
                // converting back to Date object from lightFormat string
                savedCart.delivery.deliveryDate && this.$store.commit('customer/deliveryDate', new Date(savedCart.delivery.deliveryDate))
                this.$store.commit('customer/placementInstructions', savedCart.delivery.placementInstructions)
                this.$store.commit('project/selectedProjectType', savedCart.project.selectedProjectType)
                this.$store.commit('project/selectedFillType', savedCart.project.selectedFillType)
                this.$store.commit('project/selectedDebrisType', savedCart.project.selectedDebrisType)
                this.$store.commit('onPoint/onPoint', savedCart.on_point)
                this.$store.commit('customer/selectedCustomerType', cart.selectedCustomerType)
                this.$store.commit('customer/company', cart.company)
                this.$store.commit('customer/whiteGloveReason', cart.whiteGloveReason)
                if (this.$store.state.cart.discountCode) {
                  this.$store.dispatch('cart/getDiscount', { useCartItem: true })
                }
              } else {
                this.isLockRateExpiredOpen = true
              }
            } else {
              this.isLockRateExpiredOpen = true
            }
          })
          .catch(err => {
            console.log(err)
          })
      }
    },
    async checkForWebLAT () {
      const queryParams = new URLSearchParams(location.search)
      const necessaryParams = ['service-data', 'project-type', 'debris-type']
      const addressSearchInput = document.getElementById('address-search')
      if (necessaryParams.every((param) => queryParams.has(param))) {
        const serviceData = queryParams.get('service-data')

        if (serviceData) {
          try {
            const { data } = await axios.get(`${process.env.VUE_APP_SERVER}/api/getCartServiceData`,
              {
                params: {
                  'service-data': serviceData
                }
              }
            )
            const { serviceAddress, lat, lng } = data
            const [streetAddressName, city, state, postalCode] = serviceAddress.split(', ')
            const { address: streetAddress, name: streetName } = streetAddressName.match('(?<address>\\d*) (?<name>.*)').groups
            const addressObj = {
              streetAddress,
              streetName,
              state,
              postalCode,
              city,
              country: 'US',
              lat,
              lng
            }
            addressSearchInput.value = serviceAddress
            fireAddressSearchEvent(addressObj)
            if (queryParams.has('email')) this.$store.commit('customer/email', queryParams.get('email'))
            this.$store.commit('customer/serviceAddress', addressObj)
            this.$store.commit('project/selectedProjectType', queryParams.get('project-type'))
            this.$store.commit('project/selectedDebrisType', queryParams.get('debris-type').split(','))
            if (queryParams.has('fill-type')) this.$store.commit('project/selectedFillType', queryParams.get('fill-type'))
            this.$store.commit('project/showProjectTypeTiles', true)
            this.$store.commit('dumpster/showSearchingModal', true)
            // TODO: consider updating the callMako action to default to this payload if one isns't provided
            // That way this and AddressSearch can dispatch without needing to build this object first
            this.$store.dispatch('dumpster/callMako', {
              lat: this.$store.state.customer.serviceAddress.lat,
              lng: this.$store.state.customer.serviceAddress.lng,
              selectedProjectType: this.$store.state.project.selectedProjectType,
              selectedDebrisType: this.$store.state.project.selectedDebrisType,
              selectedFillType: this.$store.state.project.selectedFillType,
              address: `${this.$store.state.customer.serviceAddress.streetAddress} ${this.$store.state.customer.serviceAddress.streetName}`,
              city: this.$store.state.customer.serviceAddress.city,
              province: this.$store.state.customer.serviceAddress.state,
              zip: this.$store.state.customer.serviceAddress.postalCode
            })
              .then(() => {
                this.$store.commit('dumpster/showSearchingModal', false)
                const dumpsterSelection = document.getElementById('dumpsterSelection')
                dumpsterSelection.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
              })
          } catch (error) {
            console.error('Failed to fetch cart service data:', error)
          }
        }
      }
    }
  },
  mounted () {
    this.$store.dispatch('dumpster/holidays')
    // Because this is the app's entry point we need to keep checking to make sure GA is ready and then clear the interval when it's loaded
    const isGAReadyInterval = setInterval(() => {
      if (isGoogleEventsReady()) {
        clearInterval(isGAReadyInterval)
        this.$store.commit('customer/gaClientID', getClientID())
        const time = timestamp()
        window.gtag('event', 'Ecommerce - PageView', {
          event_category: 'Ecommerce',
          event_label: `${time}~${window.location.href}`,
          send_to: process.env.VUE_APP_UA_ID
        })

        window.gtag('event', 'Ecommerce - PageView', {
          event_category: 'Ecommerce',
          timestamp: time,
          page: 'cart',
          bd_client_id: getClientID(),
          send_to: process.env.VUE_APP_GA4_ID
        })
      }
    }, 500)
    this.returningFromSavedCart()
    this.checkForWebLAT()
  }
}
</script>
